import { ReactComponent } from '*.svg';
import {
  Breadcrumbs,
  capitalize,
  IconButton,
  IconButtonProps,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import cx from 'classnames';
import { endImpersonation } from 'js/actions';
import ContextualSupportContainer from 'js/components/support/ContextualSupportContainer';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { NotificationAction } from 'new/components/header/NotificationAction';
import { ProfileAction } from 'new/components/header/ProfileAction';
import { ReactComponent as IncognitoOff } from 'new/components/icons/incognitoOff.svg';
import { NexlaContextualMenu } from 'new/components/NexlaContextualMenu/NexlaContextualMenu';
import OrganizationTrialStatus from 'new/components/OrganizationTrialStatus';
import { Button } from 'new/elements/Button';
import { useImpersonatedUser } from 'new/hooks/adapters/useImpersonatedUser';
import { useAppContext } from 'new/hooks/useAppContext';
import { useWhitelabelConfiguration } from 'new/hooks/useWhitelabelConfiguration';
import React, { forwardRef, ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import styles from './Header.module.scss';
import { ReactComponent as IconBack } from './icons/iconBack.svg';

// Maps lowercase slug values to their display overrides
const SLUG_DISPLAY_MAP: Record<string, string> = {
  ai: 'AI',
};

const HeaderAction = styled(IconButton)(({ theme, color }) => ({
  color: color ? theme.palette[color].main + ' !important' : undefined,
  svg: {
    fill: 'var(--color-greyscale-400) !important',
  },
  padding: 4,
  '& .MuiSvgIcon-root': {
    fontSize: '2rem',
  },
  '&:hover': {
    backgroundColor: 'transparent',
    svg: {
      fill: 'var(--color-greyscale-600) !important',
    },
  },
}));

type NavActionProps = {
  Icon: typeof ReactComponent;
  title?: string;
} & IconButtonProps;

const ActionWrapper = ({ title, children }) => {
  if (title) {
    return (
      <Tooltip title={title} placement="bottom">
        <div>{children}</div>
      </Tooltip>
    );
  }
  return children;
};

export const NavAction = forwardRef<HTMLButtonElement, NavActionProps>(
  ({ Icon, title, ...rest }, ref) => {
    return (
      <ActionWrapper title={title}>
        <HeaderAction ref={ref} {...rest}>
          <Icon className="MuiSvgIcon-root" />
        </HeaderAction>
      </ActionWrapper>
    );
  },
);

export const ImpersonateAction = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const impersonatedUser = useImpersonatedUser();

  if (!impersonatedUser) {
    return null;
  }

  const onClickStop = () => {
    dispatch(endImpersonation());
  };

  return (
    <div className={styles.navBlock}>
      <Tooltip color="error" title={'Stop impersonating ' + impersonatedUser?.full_name}>
        <Button color="error" role="button" onClick={onClickStop} startIcon={<IncognitoOff />}>
          Impersonating {impersonatedUser?.full_name}
        </Button>
      </Tooltip>
    </div>
  );
};

type HeaderProps = {
  expanded?: boolean;
  onBack?: () => void;
  rightView?: ReactNode;
  backButton?: boolean;
  handleToggleExpandedView?: () => void;
};

const Header: React.FC<HeaderProps> = (props: HeaderProps) => {
  const history = useHistory();
  const { breadcrumbList, slugs, headerRightView } = useAppContext();
  const config = useWhitelabelConfiguration();
  const bgColor = config.header.bgColor;
  const { pathname } = useLocation();
  const { expanded, rightView, backButton = true } = props;
  // rightView (prop from parent, e.g. ExpandedPipelines) takes precedence over
  // headerRightView (set via context by flow edit screens); fallback to context when prop is not passed.
  const rightContent = rightView ?? headerRightView;

  const onClickBack = () => {
    if (props.onBack) {
      props.onBack();
    } else {
      history.goBack();
    }
  };

  return (
    <header
      className={styles.header}
      style={{ backgroundColor: bgColor }}
      aria-label="header-container"
    >
      <Stack direction="row" alignItems="center" gap="8px">
        {expanded ? (
          <>
            {backButton && (
              <IconButton onClick={onClickBack}>
                <IconBack />
              </IconButton>
            )}
            <NexlaContextualMenu />
          </>
        ) : null}

        <span className={styles.breadcrumbPrefix}>/</span>
        <Breadcrumbs aria-label="breadcrumb" separator=">" className={styles.breadcrumbs}>
          {breadcrumbList.map((link, idx) => {
            const lastLink = idx === breadcrumbList.length - 1;
            const rawSlug = slugs[idx]?.replaceAll('_', ' ');
            const currentSlug = rawSlug
              ? (SLUG_DISPLAY_MAP[rawSlug.toLowerCase()] ?? rawSlug)
              : rawSlug;

            if (!currentSlug) {
              return null;
            }

            if (!link) {
              return (
                <span
                  key={currentSlug}
                  className={cx(styles.breadcrumb, lastLink ? styles.breadcrumbCurrent : undefined)}
                >
                  {capitalize(currentSlug)}
                </span>
              );
            }
            return (
              <Link
                key={link}
                to={`${ROUTE_REDESIGN}${link}`}
                onClick={() => {
                  if (!lastLink) {
                    props?.handleToggleExpandedView?.();
                  }
                }}
                aria-current={lastLink ? 'page' : undefined}
                className={cx(styles.breadcrumb, lastLink ? styles.breadcrumbCurrent : undefined)}
              >
                {capitalize(currentSlug)}
              </Link>
            );
          })}
        </Breadcrumbs>
        {rightContent}
      </Stack>
      <div className={styles.nav}>
        <Stack direction="row" alignItems="center" marginRight={10}>
          <OrganizationTrialStatus type="inline" />
        </Stack>

        <ImpersonateAction />

        <ProfileAction />

        <div className={styles.navBlock}>
          <ContextualSupportContainer />
        </div>

        <NotificationAction />
      </div>
    </header>
  );
};

export default Header;
